import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Error boundary for the SPA's route content.
 *
 * The EJS app can never show this state: a page that throws is a 500 from the server, rendered by the
 * framework. In the SPA a throw during render unmounts the tree and leaves a blank screen under a working
 * header — the failure mode that reads as "the app is empty" rather than "this page broke". So the shell
 * keeps the header, says which route failed, and offers a reload.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Console, not a toast: this is for whoever is looking at the browser, and the message is the bug.
    console.error('[spa] route threw', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main>
        <section className="panel-card">
          <h2 className="t-title" style={{ marginTop: 0 }}>
            This page failed to render
          </h2>
          <p className="muted t-body">
            The rest of the app is still running — the header, the session and the API are fine, so this is
            one page's bug.
          </p>
          <pre className="api-example" style={{ margin: '10px 0 14px' }}>{String(this.state.error.message || this.state.error)}</pre>
          <button className="btn" type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
        </section>
      </main>
    );
  }
}
